import * as React from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { InputProps, Option } from 'interfaces';
import clsx from 'clsx';
import {
	CheckIcon,
	ChevronDownIcon,
	ChevronUpIcon,
} from '@heroicons/react/24/solid';
import { CypressI } from 'interfaces/cypress';
import {
	ErrorMessageInput,
	Icon,
	Spinner,
	Typography,
} from '@/components/common';
import { Asterisk } from 'lucide-react';
import { InputText } from '../input-text';
import { useForm } from 'react-hook-form';
import Icons from '@/const/icons';
import Image from 'next/image';

export interface InputListProps {
	options: Option[];
	handleChange?: (value: string) => void;
	handleSearch?: (value: string) => void;
	myDefaultValue?: string;
	optionIconClassName?: string;
	useAuxLabel?: boolean; //if true, the aux label will be show as the selected label
	loading?: boolean;
	classNameSelectedLabel?: string;
	classNameOptionsContainer?: string; //the wrapper of the options
	classNameOptionContainer?: string; //single option
	resetValue?: boolean;
	markAsRequired?: boolean;
	classNameDropdownContainer?: string;
	isSearch?: boolean;
	hasInputAdd?: boolean;
	showActionButtons?: boolean;
	setInputCreate?: (
		value: string,
		func: any,
		id?: string | number | undefined
	) => void;
	setInputDelete?: (id: string) => void;
	resetValueAfterSelecting?: boolean;
	iconIsImage?: boolean;
	noDataLabel?: string;
}
export interface InputListRef {
	reset: () => void;
	setSelected: (value: Option) => void;
	getSelected: () => Option;
}
export const InputListV2 = React.forwardRef<
	InputListRef,
	InputProps &
		InputListProps &
		CypressI &
		React.InputHTMLAttributes<HTMLInputElement>
>(
	(
		{
			options,
			myDefaultValue,
			optionIconClassName,
			className,
			classNameOptionsContainer,
			classNameOptionContainer,
			handleChange,
			setValueInput,
			classNameInput,
			classNameTitle,
			loading,
			resetValue,
			markAsRequired,
			classNameDropdownContainer,
			classNameSelectedLabel,
			isSearch,
			hasInputAdd,
			setInputCreate,
			resetValueAfterSelecting,
			setInputDelete,
			showActionButtons = false,
			iconIsImage,
			noDataLabel,
			useAuxLabel,
			...props
		},
		ref
	) => {
		const getDefaultValue = (options: Option[], value: string): Option => {
			return options.filter((opt) => opt.name === value)[0] || options[0];
		};
		const [selected, setSelected] = React.useState<Option>(
			myDefaultValue ? getDefaultValue(options, myDefaultValue) : options[0]
		);
		const [filter, setFilter] = React.useState('');
		const [inputId, setInputId] = React.useState<string | number | undefined>();

		// const [listOptions, setListOptions] = React.useState<Option[]>([]);
		const { register, watch, setValue } = useForm();

		React.useEffect(() => {
			setInputId(undefined);
		}, []);

		React.useImperativeHandle(
			ref,
			() => ({
				reset: () => setSelected(options[0]),
				setSelected: (value: Option) => setSelected(value),
				getSelected: () => selected,
			}),
			[options, selected]
		);

		React.useEffect(() => {
			handleChange && handleChange(selected.name);
			setValueInput && setValueInput(props.name, selected.name);
			resetValueAfterSelecting && setSelected(options[0]);
		}, [selected]);

		React.useEffect(() => {
			if (resetValue) {
				setSelected(options[0]);
			}
		}, [resetValue, options]);

		React.useEffect(() => {
			if (myDefaultValue) {
				setSelected(getDefaultValue(options, myDefaultValue));
			} else {
				setSelected(options[0]);
			}
		}, [myDefaultValue]);

		const clearInput = () => {
			setValue('input', '');
		};

		const filteredOptions = React.useMemo(
			() =>
				options.filter((option) =>
					(option.label as string).toLowerCase().includes(filter.toLowerCase())
				),
			[options, filter]
		);

		const inputValue = watch('input');

		return (
			<div className={clsx(className)}>
				<input
					type="text"
					// name={props.name}
					value={selected?.name}
					className="hidden"
					readOnly
					{...props.register(props.name, props.rules)}
				/>
				<Listbox value={selected} onChange={setSelected}>
					{({ open }) => (
						<>
							<Listbox.Label className="block text-left">
								<label
									htmlFor="name"
									className={clsx(
										'text-sm 3xl:text-base text-black relative',
										classNameTitle
									)}
								>
									{props.title}
									{markAsRequired && (
										<Asterisk className="w-3 h-3 absolute -right-3 top-0 text-red-500" />
									)}
								</label>
							</Listbox.Label>
							<div
								className={clsx('relative group', {
									'!cursor-not-allowed': props.disabled,
								})}
							>
								<Listbox.Button
									className={clsx(
										'mt-[5px] relative w-full text-left border border-opacity-primary flex focus:outline-none bg-white rounded-full px-6 py-3',
										!selected.placeholder && 'capitalize',
										{ 'cursor-pointer': !props.disabled },
										{
											'!border-main-red': props.error,
											'pointer-events-none group-hover:!bg-[#F4F4F4A3]':
												props.disabled,
										},
										classNameInput
									)}
								>
									<span className="flex items-center">
										<span
											className={clsx(
												'block truncate text-sm 3xl:text-base text-black',
												{
													'text-opacity-primary':
														selected.placeholder || props.disabled,
												},
												classNameSelectedLabel
											)}
										>
											{useAuxLabel ? selected.labelAux : selected.label}
										</span>
									</span>
									<span
										className={clsx(
											'absolute inset-y-0 right-5 flex items-center ml-3 pointer-events-none'
										)}
									>
										{loading && <Spinner className="mr-1" />}
										{open ? (
											<ChevronUpIcon
												width={20}
												height={20}
												className="text-black"
											/>
										) : (
											<ChevronDownIcon
												width={20}
												height={20}
												className="text-black"
											/>
										)}
									</span>
								</Listbox.Button>
								<Transition
									show={open}
									as={React.Fragment}
									leave="transition ease-in duration-100"
									leaveFrom="opacity-100"
									leaveTo="opacity-0"
									afterLeave={() => setFilter('')}
								>
									<Listbox.Options
										static
										className={clsx(
											{ hidden: props.disabled },
											classNameDropdownContainer,
											// 'p-4',
											'rounded-lg',
											'absolute mt-1 z-20 w-full bg-white border border-opacity-primary  focus:outline-none shadow-lg overflow-hidden scroll-custom'
										)}
									>
										{isSearch && (
											<InputText
												name="search"
												register={register}
												placeholder="Search"
												iconLeft={Icons.search}
												className="w-full 2xl:w-auto px-4"
												classNameIcon={clsx(
													'!top-[35%] !w-4 !h-4',
													props.classNameIcon
												)}
												classNameInput={clsx(
													'pl-9 border-none !bg-gray-15 h-8 !mt-0',
													classNameInput
												)}
												onChange={({ target }) => setFilter(target.value)}
											/>
										)}

										<div
											className={clsx(
												'overflow-auto scroll-custom',
												{
													'mt-3': isSearch,
												},
												classNameOptionsContainer
											)}
										>
											{/* the place holder is option 1 */}
											{filteredOptions.length > 0 ? (
												filteredOptions.map(
													(option, index) =>
														!option.placeholder && (
															<Listbox.Option
																key={option.name + '-' + index}
																className={({ active }) =>
																	clsx(
																		(selected?.name === option.name ||
																			active) &&
																			'bg-dark-10',
																		'relative pl-4 py-3  cursor-pointer select-none capitalize',
																		classNameOptionContainer
																	)
																}
																value={option}
															>
																<div className="flex items-center gap-2 group">
																	{iconIsImage && option.icon ? (
																		<Image
																			src={option.icon}
																			width={16}
																			height={16}
																			className={clsx(
																				'size-4',
																				optionIconClassName,
																				option.classNameItemIcon
																			)}
																			alt="option-icon"
																			unoptimized
																		/>
																	) : (
																		<Icon
																			src={option.icon || ''}
																			className={clsx(
																				'size-4',
																				optionIconClassName,
																				option.classNameItemIcon
																			)}
																		/>
																	)}
																	<div className="relative flex gap-[10px]">
																		<span className="block truncate text-base text-black">
																			{option.label}
																		</span>
																		<small
																			className={clsx('text-purple-150 -mt-1', {
																				hidden: !option.flag,
																			})}
																		>
																			New
																		</small>
																	</div>

																	{selected?.name === option.name ? (
																		<span className="absolute right-0 pr-4">
																			<CheckIcon
																				className="w-5 h-5 text-black"
																				aria-hidden="true"
																			/>
																		</span>
																	) : null}
																	<div
																		className={clsx(
																			'flex items-center gap-1 absolute right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200',
																			{
																				hidden:
																					selected?.name === option.name ||
																					!showActionButtons,
																			}
																		)}
																	>
																		<button
																			type="button"
																			onClick={(e) => {
																				e.stopPropagation();
																				setValue('input', option.label);
																				setInputId(option.name);
																			}}
																		>
																			<Icon
																				src={Icons.editTwo}
																				className="!size-4"
																			/>
																		</button>
																		<button
																			type="button"
																			onClick={(e) => {
																				e.stopPropagation();
																				if (setInputDelete) {
																					setInputDelete(option.name);
																				}
																			}}
																		>
																			<Icon
																				src={Icons.trash}
																				className="!size-4 text-main-red"
																			/>
																		</button>
																	</div>
																</div>
															</Listbox.Option>
														)
												)
											) : (
												<Typography type="regular-16" className="mt-2">
													{noDataLabel || 'No Data'}
												</Typography>
											)}
										</div>

										{hasInputAdd && (
											<InputText
												name="input"
												title="Others (please specify)"
												register={register}
												placeholder="Write the name here"
												className="w-full 2xl:w-auto mt-4 px-4"
												classNameTitle="!text-[#6E6A73]"
												classNameIcon={clsx('!top-[53%] !size-6')}
												classNameInput={clsx('')}
												setValueInput={setValue}
												iconRight={Icons.sendPurple}
												rightClick={() => {
													if (setInputCreate) {
														setInputCreate(inputValue, clearInput, inputId);
													}
												}}
												onKeyDown={(event) => {
													event.stopPropagation();
													if (event.key === 'Enter' && setInputCreate) {
														event.preventDefault();
														const inputValue = (
															event.target as HTMLInputElement
														).value;
														setInputCreate(inputValue, clearInput, inputId);
													}
												}}
											/>
										)}
									</Listbox.Options>
								</Transition>
							</div>
						</>
					)}
				</Listbox>
				{props.error && props.error.message && (
					<ErrorMessageInput message={props.error.message} />
				)}
			</div>
		);
	}
);
InputListV2.displayName = 'InputListV2';
